import { getUserInfo } from '@/utils/useLocalStorage';

import { hasExplicitModelSubscriptionSwitchFields, readModelSubscriptionFormSwitches } from '../types/detail';
import type { MsApiPayload, MsSpec } from './schema';

function normalizeStringList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

function normalizePositiveNumber(value: unknown, enabled: boolean) {
  if (!enabled) return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/** 判断可选额度字段是否填写了有效的正整数 */
function hasPositiveNumber(value: unknown) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0;
}

function readSwitches(values: Record<string, unknown>, specSwitches?: MsSpec['switches']) {
  const explicit = hasExplicitModelSubscriptionSwitchFields(values) ? readModelSubscriptionFormSwitches(values) : undefined;
  const showRequests = explicit?.showRequests ?? specSwitches?.showRequests;
  const showInputTokens = explicit?.showInputTokens ?? specSwitches?.showInputTokens;
  const showOutputTokens = explicit?.showOutputTokens ?? specSwitches?.showOutputTokens;

  return {
    // 限额开关：总量与输入/输出配额由 resourceLimits 字段值本身表达，不再依赖 showTokenLimit
    showTokenLimit: false,
    showRequests: Boolean(showRequests ?? hasPositiveNumber(values.requests)),
    showInputTokens: Boolean(
      showInputTokens ?? (hasPositiveNumber(values.inputTokensValue) || hasPositiveNumber(values.inputTokensWindowSeconds)),
    ),
    showOutputTokens: Boolean(
      showOutputTokens ?? (hasPositiveNumber(values.outputTokensValue) || hasPositiveNumber(values.outputTokensWindowSeconds)),
    ),
  };
}

function resolveStrategyName(values: Record<string, unknown>) {
  return String(values.strategyName || '').trim();
}

/** 默认关闭的 rateLimits / resourceLimits 结构（与 API 契约一致） */
export function createMsDefaultLimits(): Pick<MsApiPayload, 'rateLimits' | 'resourceLimits'> {
  return {
    rateLimits: {
      inputTokens: { enabled: false, value: 0, windowSeconds: 0 },
      outputTokens: { enabled: false, value: 0, windowSeconds: 0 },
    },
    resourceLimits: {
      billingTotalTokens: { enabled: false, value: 0 },
      inputTokens: { enabled: false, value: 0 },
      outputTokens: { enabled: false, value: 0 },
      requests: { enabled: false, value: 0 },
    },
  };
}

function buildMsRateLimits(values: Record<string, unknown>, switches: ReturnType<typeof readSwitches>): MsApiPayload['rateLimits'] {
  const inputTokensEnabled = Boolean(switches.showInputTokens);
  const outputTokensEnabled = Boolean(switches.showOutputTokens);

  return {
    inputTokens: {
      enabled: inputTokensEnabled,
      value: normalizePositiveNumber(values.inputTokensValue, inputTokensEnabled),
      windowSeconds: normalizePositiveNumber(values.inputTokensWindowSeconds, inputTokensEnabled),
    },
    outputTokens: {
      enabled: outputTokensEnabled,
      value: normalizePositiveNumber(values.outputTokensValue, outputTokensEnabled),
      windowSeconds: normalizePositiveNumber(values.outputTokensWindowSeconds, outputTokensEnabled),
    },
  };
}

function buildMsResourceLimits(values: Record<string, unknown>, switches: ReturnType<typeof readSwitches>): MsApiPayload['resourceLimits'] {
  const requestsEnabled = Boolean(switches.showRequests);
  // billingTotalTokens 与 input/output 互斥：显式计价态优先；未标记时仍总量优先
  const useSubResource = values.useSubResourcePricing;
  const billingTotalEnabled = useSubResource !== true && hasPositiveNumber(values.billingTotalTokens);
  const inputQuotaEnabled = useSubResource !== false && !billingTotalEnabled && hasPositiveNumber(values.inputTokens);
  const outputQuotaEnabled = useSubResource !== false && !billingTotalEnabled && hasPositiveNumber(values.outputTokens);

  return {
    billingTotalTokens: {
      enabled: billingTotalEnabled,
      value: normalizePositiveNumber(values.billingTotalTokens, billingTotalEnabled),
    },
    inputTokens: {
      enabled: inputQuotaEnabled,
      value: normalizePositiveNumber(values.inputTokens, inputQuotaEnabled),
    },
    outputTokens: {
      enabled: outputQuotaEnabled,
      value: normalizePositiveNumber(values.outputTokens, outputQuotaEnabled),
    },
    requests: {
      enabled: requestsEnabled,
      value: normalizePositiveNumber(values.requests, requestsEnabled),
    },
  };
}

/** 将表单值与开关状态归一化为 API 提交体（与 deliveryMetadata / 直建提交一致） */
export function buildMsSubscriptionPayload(
  values: Record<string, unknown>,
  options: {
    showTokenLimit?: boolean;
    showRequests?: boolean;
    showInputTokens?: boolean;
    showOutputTokens?: boolean;
    creator?: string;
  } = {},
): MsApiPayload {
  const creator = options.creator || getUserInfo()?.name || getUserInfo()?.uuid || '';
  const switches = readSwitches(values, {
    showTokenLimit: options.showTokenLimit,
    showRequests: options.showRequests,
    showInputTokens: options.showInputTokens,
    showOutputTokens: options.showOutputTokens,
  });
  const strategyName = resolveStrategyName(values);

  return {
    alias: String(values.alias || '').trim(),
    workspace: String(values.workspace || ''),
    allowedClientCIDRs: normalizeStringList(values.allowedClientCIDRs),
    allowedClientIPs: normalizeStringList(values.allowedClientIPs),
    creator,
    description: String(values.description || '').trim(),
    llmgwStrategyRef: {
      external: {
        strategyName,
      },
    },
    rateLimits: buildMsRateLimits(values, switches),
    resourceLimits: buildMsResourceLimits(values, switches),
  };
}

export function readMsSwitchesFromForm(values: Record<string, unknown>) {
  return readSwitches(values);
}

export { readSwitches as readMsSwitches };
